import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Select from '../../components/common/Select';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ReconciliationTab from '../../components/votes/Reconciliationtab'; // ← adjust path if needed
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import paymentService from '../../api/services/paymentService';
import contestService from '../../api/services/contestService';
import voteService from '../../api/services/voteService';
import { handleApiError, formatDate, formatCurrency } from '../../utils/helpers';
import axios from 'axios';

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = {
  PAYMENTS: 'payments',
  RECONCILIATION: 'reconciliation',
};

// ─── Small reusable modal shell ───────────────────────────────────────────────
const Modal = ({ children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const PaymentsPage = () => {
  const { error: showError, success: showSuccess } = useToast();
  const { user } = useAuth();

  // ── Active tab ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(TABS.PAYMENTS);

  // ── Shared data ─────────────────────────────────────────────────────────────
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState('');
  const [overallRevenue, setOverallRevenue] = useState(0);
  const [loadingOverallRevenue, setLoadingOverallRevenue] = useState(true);

  // ── Payments tab state ──────────────────────────────────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [completingPayment, setCompletingPayment] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(false);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
  const isCompanyAdmin = user?.role === USER_ROLES.COMPANY_ADMIN;
  // const canManuallyComplete = isSuperAdmin || isCompanyAdmin;
  const canManuallyComplete = isSuperAdmin;
  const canDelete = isSuperAdmin || isCompanyAdmin;
  const selectedContestData = contests.find(c => c.id === selectedContest);
  const colSpan = (canDelete || canManuallyComplete) ? 9 : 7;

  // ── Client-side filter ───────────────────────────────────────────────────────
  // const filteredPayments = React.useMemo(() => {
  //   if (!Array.isArray(payments)) return [];
  //   return payments.filter(p => {
  //     if (!p) return false;
  //     if (statusFilter && p.status !== statusFilter) return false;
  //     if (methodFilter && p.method !== methodFilter) return false;
  //     if (searchTerm) {
  //       const q = searchTerm.toLowerCase();
  //       const hit =
  //         p.reference?.toLowerCase().includes(q) ||
  //         p.vote?.voterName?.toLowerCase().includes(q) ||
  //         p.vote?.voterEmail?.toLowerCase().includes(q) ||
  //         p.phoneNumber?.toLowerCase().includes(q) ||
  //         p.vote?.contestant?.name?.toLowerCase().includes(q);
  //       if (!hit) return false;
  //     }
  //     return true;
  //   });
  // }, [payments, searchTerm, statusFilter, methodFilter]);

  const filteredPayments = React.useMemo(() => {
    if (!Array.isArray(payments)) return [];
    return payments.filter(p => {
      if (!p) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (methodFilter && p.method !== methodFilter) return false;
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (new Date(p.createdAt) < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(p.createdAt) > to) return false;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const hit =
          p.reference?.toLowerCase().includes(q) ||
          p.vote?.voterName?.toLowerCase().includes(q) ||
          p.vote?.voterEmail?.toLowerCase().includes(q) ||
          p.phoneNumber?.toLowerCase().includes(q) ||
          p.vote?.contestant?.name?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [payments, searchTerm, statusFilter, methodFilter, dateFrom, dateTo]);

  // ── Fetch helpers ────────────────────────────────────────────────────────────
  const fetchContests = useCallback(async () => {
    try {
      setLoading(true);
      const response = isSuperAdmin
        ? await contestService.getAllContests({ pageSize: 100 })
        : await contestService.getUserContests({ pageSize: 100 });
      if (response.success) {
        const list = response.data || response.contests || [];
        setContests(list);
        if (list.length > 0) setSelectedContest(list[0].id);
        fetchOverallRevenue(list);
      }
    } catch (err) {
      setError(handleApiError(err));
      setLoading(false);
    }
  }, [isSuperAdmin]);

  const fetchOverallRevenue = async (list) => {
    setLoadingOverallRevenue(true);
    let total = 0;
    for (const contest of list) {
      try {
        const res = await paymentService.getPaymentStats(contest.id);
        if (res.success && res.data) total += res.data.totalRevenue || 0;
      } catch (_) { }
    }
    setOverallRevenue(total);
    setLoadingOverallRevenue(false);
  };

  const fetchPayments = useCallback(async () => {
    if (!selectedContest) return;
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getContestPayments(selectedContest, {
        status: statusFilter || undefined,
        method: methodFilter || undefined,
        page: currentPage,
        pageSize: 25,
      });
      if (response.success) {
        setPayments(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalPayments(response.pagination.total || response.data?.length || 0);
        } else {
          setTotalPages(1);
          setTotalPayments(response.data?.length || 0);
        }
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedContest, statusFilter, methodFilter, currentPage]);

  const fetchPaymentStats = useCallback(async () => {
    if (!selectedContest) return;
    try {
      const res = await paymentService.getPaymentStats(selectedContest);
      if (res.success) setPaymentStats(res.data || null);
    } catch (_) { }
  }, [selectedContest]);

  // ── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => { fetchContests(); }, []);

  useEffect(() => {
    if (selectedContest && activeTab === TABS.PAYMENTS) {
      fetchPayments();
      fetchPaymentStats();
    }
  }, [selectedContest, statusFilter, methodFilter, currentPage, activeTab]);

  // useEffect(() => { setCurrentPage(1); }, [selectedContest, statusFilter, methodFilter]);
  useEffect(() => { setCurrentPage(1); }, [selectedContest, statusFilter, methodFilter, dateFrom, dateTo]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleManualComplete = async () => {
    if (!selectedPayment) return;
    try {
      setCompletingPayment(true);
      const token = localStorage.getItem('nfa_token');
      await axios.request({
        method: 'post',
        url: `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/payments/manual-complete/${selectedPayment.reference}`,
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess('Payment marked as completed successfully!');
      setShowCompleteModal(false);
      setSelectedPayment(null);
      await fetchPayments();
      await fetchPaymentStats();
      if (contests.length > 0) fetchOverallRevenue(contests);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to complete payment manually');
    } finally {
      setCompletingPayment(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!selectedPayment) return;
    try {
      setDeletingPayment(true);
      const voteId = selectedPayment.voteId || selectedPayment.vote?.id;
      if (voteId) {
        await voteService.deleteVote(voteId);
      } else {
        await paymentService.deletePayment(selectedPayment.id);
      }
      showSuccess('Payment and associated vote deleted successfully.');
      setShowDeleteModal(false);
      setSelectedPayment(null);
      await fetchPayments();
      await fetchPaymentStats();
      if (contests.length > 0) fetchOverallRevenue(contests);
    } catch (err) {
      showError(err.response?.data?.message || handleApiError(err) || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };

  // ── Styling helpers ───────────────────────────────────────────────────────────
  const getStatusColor = (status) => ({
    COMPLETED: 'bg-green-100 text-green-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    FAILED: 'bg-red-100 text-red-800',
  }[status] || 'bg-gray-100 text-gray-800');

  const getMethodBadge = (method) => ({
    PAYSTACK: 'bg-blue-100 text-blue-800',
    USSD: 'bg-orange-100 text-orange-800',
    MANUAL: 'bg-purple-100 text-purple-800',
  }[method] || 'bg-gray-100 text-gray-800');

  // ── Derived stats ─────────────────────────────────────────────────────────────
  const completedPayments = filteredPayments.filter(p => p?.status === 'COMPLETED');
  const pendingPayments = filteredPayments.filter(p => p?.status === 'PENDING');
  const totalRevenue = completedPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const averageTransaction = completedPayments.length > 0 ? totalRevenue / completedPayments.length : 0;

  // ── Loading shell ─────────────────────────────────────────────────────────────
  if (loading && contests.length === 0) {
    return (
      <DashboardLayout title={isSuperAdmin ? 'All Payments' : 'My Payments'}>
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading payments..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isSuperAdmin ? 'All Payments' : 'My Payments'}
      subtitle={
        isSuperAdmin
          ? 'View all payment transactions in the system'
          : 'View payment transactions for your contests'
      }
    >
      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {showCompleteModal && selectedPayment && (
        <Modal>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Confirm Manual Completion</h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            Are you sure you want to manually mark this payment as completed?
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-2 text-sm">
            <span className="text-gray-600">Reference:</span>
            <span className="font-mono font-semibold">{selectedPayment.reference}</span>
            <span className="text-gray-600">Amount:</span>
            <span className="font-semibold">{formatCurrency(selectedPayment.amount)}</span>
            <span className="text-gray-600">Voter:</span>
            <span className="font-semibold">{selectedPayment.vote?.voterName || 'N/A'}</span>
            <span className="text-gray-600">Status:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${getStatusColor(selectedPayment.status)}`}>
              {selectedPayment.status}
            </span>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-5 text-xs text-yellow-800">
            <strong>Warning:</strong> This will mark the payment as completed and process the associated votes.
            Make sure payment has been received before proceeding.
          </div>
          <div className="flex space-x-3">
            <button onClick={() => { setShowCompleteModal(false); setSelectedPayment(null); }}
              disabled={completingPayment}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleManualComplete} disabled={completingPayment}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center">
              {completingPayment ? <><Loader size="sm" className="mr-2" />Completing…</> : 'Yes, Complete Payment'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteModal && selectedPayment && (
        <Modal>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Payment</h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            This will permanently delete the payment record and its associated vote.
            Contestant vote counts will be recalculated automatically.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-2 text-sm">
            <span className="text-gray-600">Reference:</span>
            <span className="font-mono font-semibold">{selectedPayment.reference}</span>
            <span className="text-gray-600">Amount:</span>
            <span className="font-semibold">{formatCurrency(selectedPayment.amount)}</span>
            <span className="text-gray-600">Contestant:</span>
            <span className="font-semibold">{selectedPayment.vote?.contestant?.name || 'N/A'}</span>
            <span className="text-gray-600">Votes:</span>
            <span className="font-semibold">{selectedPayment.vote?.voteCount || 0}</span>
            <span className="text-gray-600">Status:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${getStatusColor(selectedPayment.status)}`}>
              {selectedPayment.status}
            </span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-xs text-red-800">
            <strong>This action cannot be undone.</strong> The vote count for{' '}
            <strong>{selectedPayment.vote?.contestant?.name}</strong> will be corrected automatically.
          </div>
          <div className="flex space-x-3">
            <button onClick={() => { setShowDeleteModal(false); setSelectedPayment(null); }}
              disabled={deletingPayment}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleDeletePayment} disabled={deletingPayment}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center">
              {deletingPayment ? <><Loader size="sm" className="mr-2" />Deleting…</> : 'Yes, Delete'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Overall Revenue Banner (always visible) ───────────────────────── */}
      {/* <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">Overall Total Revenue</p>
            <p className="text-sm text-gray-500 mb-2">
              Across all {contests.length} contest{contests.length !== 1 ? 's' : ''}
            </p>
            {loadingOverallRevenue ? (
              <div className="flex items-center">
                <Loader size="sm" /><span className="ml-2 text-gray-500">Calculating…</span>
              </div>
            ) : (
              <p className="text-4xl font-bold text-green-600">{formatCurrency(overallRevenue)}</p>
            )}
          </div>
          <div className="bg-green-200 p-4 rounded-lg">
            <svg className="w-12 h-12 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </Card> */}

      {/* ── Shared contest + filter selectors (always visible) ───────────── */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Select
            label="Select Contest"
            value={selectedContest}
            onChange={(e) => setSelectedContest(e.target.value)}
            options={contests.map(c => ({ value: c.id, label: c.title }))}
            placeholder="Select a contest"
          />
          <Select
            label="Filter by Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'FAILED', label: 'Failed' },
            ]}
          />
          <Select
            label="Filter by Method"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            options={[
              { value: '', label: 'All Methods' },
              { value: 'PAYSTACK', label: 'Paystack' },
              { value: 'USSD', label: 'USSD / MoMo' },
              { value: 'MANUAL', label: 'Manual' },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
      <input
        type="date"
        value={dateTo}
        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="flex items-end">
      {(dateFrom || dateTo || statusFilter || methodFilter || searchTerm) && (
        <button
          onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter(''); setMethodFilter(''); setSearchTerm(''); setCurrentPage(1); }}
          className="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Clear All Filters
        </button>
      )}
    </div>
  </div>
      </Card>

      {/* ── Tab navigation ────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">

          {/* Transactions tab */}
          <button
            onClick={() => setActiveTab(TABS.PAYMENTS)}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === TABS.PAYMENTS
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Transactions
            {payments.length > 0 && (
              <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                {payments.length}
              </span>
            )}
          </button>

          {/* Reconciliation tab */}
          <button
            onClick={() => setActiveTab(TABS.RECONCILIATION)}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === TABS.RECONCILIATION
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reconciliation
            {/* Badge shows pending count as a nudge to run reconciliation */}
            {pendingPayments.length > 0 && (
              <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                {pendingPayments.length} pending
              </span>
            )}
          </button>

        </nav>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: TRANSACTIONS
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === TABS.PAYMENTS && (
        <>
          {selectedContest && (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  {selectedContestData?.title} — Statistics
                  {(statusFilter || methodFilter || searchTerm) && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({filteredPayments.length} result{filteredPayments.length !== 1 ? 's' : ''} shown)
                    </span>
                  )}
                </h3>
              </div>

              {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                {[
                  { label: 'Contest Revenue',  value: formatCurrency(totalRevenue),       color: 'green',  icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { label: 'Completed',        value: completedPayments.length,           color: 'blue',   icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { label: 'Pending',          value: pendingPayments.length,             color: 'yellow', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { label: 'Avg. Transaction', value: formatCurrency(averageTransaction), color: 'purple', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
                ].map(({ label, value, color, icon }) => (
                  <Card key={label}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{label}</p>
                        <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
                      </div>
                      <div className={`bg-${color}-100 p-3 rounded-lg`}>
                        <svg className={`w-8 h-8 text-${color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
                        </svg>
                      </div>
                    </div>
                  </Card>
                ))}
              </div> */}

              {paymentStats && (
                <Card className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Payment Method</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paymentStats.paymentsByMethod?.map((m, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMethodBadge(m.method)}`}>
                            {m.method}
                          </span>
                          <span className="text-sm text-gray-600">{m._count} transaction{m._count !== 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(m._sum?.amount || 0)}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}

          {error && <ErrorMessage message={error} onRetry={fetchPayments} className="mb-6" />}

          {loading ? (
            <Card>
              <div className="flex items-center justify-center py-12">
                <Loader size="md" text="Loading payments..." />
              </div>
            </Card>
          ) : payments.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Yet</h3>
                <p className="text-gray-600">
                  {selectedContestData
                    ? `"${selectedContestData.title}" hasn't received any payments yet`
                    : "This contest hasn't received any payments yet"}
                </p>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Payment Transactions {selectedContestData && `— ${selectedContestData.title}`}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Showing <span className="font-medium text-gray-700">{filteredPayments.length}</span>
                    {filteredPayments.length !== payments.length && (
                      <> of <span className="font-medium text-gray-700">{payments.length}</span></>
                    )} payment{payments.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <input
                  type="text"
                  placeholder="Search reference, voter, email, phone, contestant…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-96"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Reference', 'Voter Information', 'Contestant', 'Amount', 'Method', 'Status', 'Date',
                        ...(canManuallyComplete || canDelete ? ['Actions'] : [])
                      ].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPayments.length > 0 ? filteredPayments.map(payment => {
                      if (!payment?.id) return null;
                      return (
                        <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-mono text-gray-900">{payment.reference || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{payment.vote?.voterName || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{payment.vote?.voterEmail || payment.phoneNumber || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{payment.vote?.contestant?.name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{payment.vote?.voteCount || 0} vote{payment.vote?.voteCount !== 1 ? 's' : ''}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getMethodBadge(payment.method)}`}>
                              {payment.method || 'UNKNOWN'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                              {payment.status || 'UNKNOWN'}
                            </span>
                          </td>
                          {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(payment.createdAt)}
                          </td> */}
                          <td className="px-6 py-4 whitespace-nowrap">
  <div className="text-sm text-gray-900">{formatDate(payment.createdAt)}</div>
  <div className="text-xs text-gray-500">
    {payment.createdAt ? new Date(payment.createdAt).toLocaleTimeString('en-GH', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    }) : '—'}
  </div>
</td>
                          {(canManuallyComplete || canDelete) && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {canManuallyComplete && payment.status === 'PENDING' && (
                                  <button
                                    onClick={() => { setSelectedPayment(payment); setShowCompleteModal(true); }}
                                    className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Complete
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => { setSelectedPayment(payment); setShowDeleteModal(true); }}
                                    className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={colSpan} className="px-6 py-8 text-center text-gray-500">
                          No payments found matching your criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-right font-semibold text-gray-900">Total:</td>
                      <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-green-600">
                        {formatCurrency(totalRevenue)}
                      </td>
                      <td colSpan={colSpan - 4} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages} ({totalPayments} total)
                  </p>
                  <div className="flex space-x-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                      Previous
                    </button>
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = idx + 1;
                      else if (currentPage <= 3) pageNum = idx + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + idx;
                      else pageNum = currentPage - 2 + idx;
                      return (
                        <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 border rounded-md text-sm font-medium ${currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}>
                          {pageNum}
                        </button>
                      );
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: RECONCILIATION
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === TABS.RECONCILIATION && (
        <ReconciliationTab
          selectedContest={selectedContest}
          contests={contests}
        />
      )}

    </DashboardLayout>
  );
};

export default PaymentsPage;